/**
 * SOAP Note Export Utilities
 * Handles printing and PDF export of SOAP notes
 */

import html2pdf from 'html2pdf.js';

/**
 * Convert markdown to HTML
 * Converts bold (**text**), italics (*text*), and line breaks to HTML
 */
const markdownToHtml = (text) => {
  if (!text) return '';

  return text
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italics: *text* -> <em>text</em>
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Line breaks: newline -> <br>
    .replace(/\n/g, '<br>');
};

/**
 * Export SOAP note as PDF
 * Creates a downloadable PDF file of the SOAP note
 */
export const exportSOAPNoteToPDF = ({
  patientName,
  appointmentDate,
  fileName,
  soapNote,
  dentistName,
  licenseNumber,
}) => {
  try {
    // Format the appointment date.
    // Parse as local date to avoid UTC-to-local timezone shift (e.g. "2026-03-03" becoming March 2nd).
    const formattedDate = (() => {
      if (!appointmentDate) return '';
      const [year, month, day] = String(appointmentDate).split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    })();

    // Create container element
    const element = document.createElement('div');
    element.style.padding = '20px';
    element.style.fontFamily = 'Arial, sans-serif';
    element.style.maxWidth = '210mm';
    element.style.margin = '0 auto';
    element.style.width = '100%';
    element.innerHTML = `
      <div style="color: #1a1a1a; line-height: 1.7;">
        <div style="text-align: center; padding: 40px 0 50px 0; border-bottom: 2px solid #000;">
          <h1 style="margin: 0 0 8px 0; font-size: 32px; font-weight: 700; letter-spacing: 1px;">SOAP NOTE</h1>
          <p style="margin: 0 0 30px 0; font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: 0.8px;">Clinical Documentation</p>

          <div style="margin-bottom: 20px; font-size: 13px; line-height: 1.8;">
            <div style="margin: 3px 0;"><strong>${patientName || 'Patient Name'}</strong></div>
            <div style="margin: 3px 0; color: #555;">${formattedDate || 'Date'}</div>
            ${fileName ? `<div style="margin: 3px 0; color: #555;">${fileName}</div>` : ''}
          </div>

          ${dentistName || licenseNumber ? `
            <div style="margin-top: 25px; padding-top: 25px; border-top: 1px solid #ddd;">
              ${dentistName ? `<div style="margin: 3px 0; font-size: 13px;"><strong>Dr. ${dentistName}</strong></div>` : ''}
              ${licenseNumber ? `<div style="margin: 3px 0; font-size: 12px; color: #666;">License #: ${licenseNumber}</div>` : ''}
            </div>
          ` : ''}
        </div>

        ${soapNote?.soap_note ? `
          <div>
            <div style="margin-top: 25px;">
              <h2 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; padding-bottom: 5px;">Subjective</h2>
              <div style="padding: 0; font-size: 13px; line-height: 1.8;">${markdownToHtml(soapNote.soap_note.subjective) || 'N/A'}</div>
            </div>

            <div style="margin-top: 20px;">
              <h2 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; padding-bottom: 5px;">Objective</h2>
              <div style="padding: 0; font-size: 13px; line-height: 1.8;">${markdownToHtml(soapNote.soap_note.objective) || 'N/A'}</div>
            </div>

            <div style="margin-top: 20px;">
              <h2 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; padding-bottom: 5px;">Assessment</h2>
              <div style="padding: 0; font-size: 13px; line-height: 1.8;">${markdownToHtml(soapNote.soap_note.assessment) || 'N/A'}</div>
            </div>

            <div style="margin-top: 20px;">
              <h2 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; padding-bottom: 5px;">Plan</h2>
              <div style="padding: 0; font-size: 13px; line-height: 1.8;">${markdownToHtml(soapNote.soap_note.plan) || 'N/A'}</div>
            </div>
          </div>
        ` : ''}

        ${soapNote?.clinical_support?.codes && soapNote.clinical_support.codes.length > 0 ? `
          <div style="margin-top: 25px;">
            <h2 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #000; padding-bottom: 5px;">Billing Codes</h2>
            <div>
              ${soapNote.clinical_support.codes.map(code => `
                <div style="margin: 12px 0; padding: 8px 0; border-bottom: 1px solid #eee; font-size: 12px;">
                  <div style="margin: 2px 0;"><strong>Code:</strong> ${code.code}</div>
                  <div style="margin: 2px 0; color: #333;"><strong>Description:</strong> ${code.description}</div>
                  <div style="margin: 2px 0; color: #555; font-size: 11px;"><strong>Timing:</strong> ${code.timing || 'N/A'}</div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <div style="margin-top: 40px; padding-top: 15px; border-top: 2px solid #000; text-align: center; color: #777; font-size: 10px; letter-spacing: 0.3px;">
          <p style="margin: 0;">Generated by Algodentra • ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;

    // PDF options
    const options = {
      margin: [10, 10, 10, 10],
      filename: `SOAP_Note_${patientName?.replace(/\s+/g, '_') || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1.5, useCORS: true, allowTaint: true },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    // Generate PDF
    html2pdf().set(options).from(element).save();
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    alert('Failed to export PDF. Please try again.');
  }
};
